import { useState, type ReactNode } from 'react';
import { Icon } from '../icons';
import type { DuplicateDetection } from '../api';
import * as api from '../api';
import { StatusBadge, SourceIcon } from './StatusBadge';
import { detectionsForMember, groupDuplicates, type DuplicateGroup } from '../utils/duplicate-groups';

function formatDate(iso: string): string {
  const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function DupIdeaCard({
  number,
  title,
  body,
  status,
  source,
  createdAt,
  role,
  onOpen,
  onRemove,
  busy = false,
}: {
  number: number;
  title: string;
  body: string | null;
  status: string;
  source: string;
  createdAt: string;
  role: 'keep' | 'archive';
  onOpen: () => void;
  /** Group of 3+: take this idea out of the group ("not a duplicate"). */
  onRemove?: () => void;
  busy?: boolean;
}) {
  return (
    <div className="dup-idea-card" role="button" tabIndex={0} onClick={onOpen} title="Open idea"
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } }}>
      <div className="dup-idea-card-top">
        <span className="dup-idea-num mono">#{number}</span>
        <span className={`dup-idea-role dup-idea-role-${role}`}>
          {role === 'keep' ? 'Keeps' : 'Folds in'}
        </span>
      </div>
      <div className="dup-idea-title">{title}</div>
      {body && <div className="dup-idea-body">{body}</div>}
      <div className="dup-idea-meta">
        <StatusBadge status={status} />
        <span className="dup-idea-meta-right">
          <SourceIcon source={source} /> {formatDate(createdAt)}
        </span>
      </div>
      {onRemove && (
        <button className="dup-idea-remove" disabled={busy}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Not a duplicate of the others — keep it out of this group">
          not a duplicate
        </button>
      )}
    </div>
  );
}

/** Merge every other member of the group into its keeper, one at a time. */
export async function mergeDuplicateGroup(group: DuplicateGroup): Promise<void> {
  for (const member of group.members.slice(1)) {
    await api.mergeIdeas(group.keeper.id, member.id);
  }
}

export async function dismissDetections(detections: { id: number }[]): Promise<void> {
  await Promise.all(detections.map((d) => api.dismissDuplicate(d.id)));
}


export function DuplicatesView({
  duplicates,
  onChanged,
  onSelect,
}: {
  duplicates: DuplicateDetection[];
  /** Called after a merge or dismissal so the app can refetch. */
  onChanged: (merged: boolean) => void;
  onSelect: (ideaId: number) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const groups = groupDuplicates(duplicates);

  const act = async (key: string, merged: boolean, fn: () => Promise<void>) => {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
      // Refetch even after a failure: part of a group may have merged.
      onChanged(merged);
    }
  };

  return (
    <div className="duplicates-view">
      <div className="dup-queue-header">
        <h2 className="dup-queue-title">
          Duplicate review
          {groups.length > 0 && (
            <span className="dup-queue-count">
              {groups.length} {groups.length === 1 ? 'group' : 'groups'} pending
            </span>
          )}
        </h2>
        <p className="dup-queue-desc">
          Ideas that look semantically similar. Merging combines notes and tags into the newest
          idea and archives the others — nothing is deleted.
        </p>
      </div>

      {error && <div className="review-error">{error}</div>}

      {groups.length === 0 ? (
        <div className="empty-state">
          <p>No pending duplicates.</p>
          <p className="empty-state-sub">
            New captures are checked automatically — potential duplicates will show up here.
          </p>
        </div>
      ) : (
        <div className="dup-queue-list">
          {groups.map((group) => {
            const busy = busyKey === group.key;
            const pair = group.members.length === 2;
            return (
              <div key={group.key} className="dup-card">
                <div className="dup-card-top">
                  <span className="dup-sim">
                    {pair ? '' : `${group.members.length} ideas · `}
                    {pair ? '' : 'up to '}{(group.topScore * 100).toFixed(0)}% match
                  </span>
                  <span className="dup-detected">detected {formatDate(group.detectedAt)}</span>
                </div>
                <div className={pair ? 'dup-pair' : 'dup-group'}>
                  {group.members.map((m, i) => (
                    <DupIdeaCardSlot key={m.id} glyph={pair && i === 1}>
                      <DupIdeaCard
                        number={m.number}
                        title={m.title}
                        body={m.body}
                        status={m.status}
                        source={m.source}
                        createdAt={m.created_at}
                        role={i === 0 ? 'keep' : 'archive'}
                        onOpen={() => onSelect(m.id)}
                        busy={busy}
                        onRemove={pair ? undefined : () =>
                          act(group.key, false, () => dismissDetections(detectionsForMember(group, m.id)))}
                      />
                    </DupIdeaCardSlot>
                  ))}
                </div>
                <div className="dup-card-actions">
                  <button className="btn btn-sm btn-primary" disabled={busy}
                    onClick={() => act(group.key, true, () => mergeDuplicateGroup(group))}>
                    {pair ? 'Merge' : `Merge all into #${group.keeper.number}`}
                  </button>
                  <button className="btn btn-sm btn-secondary" disabled={busy}
                    onClick={() => act(group.key, false, () => dismissDetections(group.detections))}>
                    {pair ? 'Not a duplicate' : 'None are duplicates'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DupIdeaCardSlot({ glyph, children }: { glyph: boolean; children: ReactNode }) {
  return (
    <>
      {glyph && <span className="dup-pair-glyph" aria-hidden="true"><Icon name="merge" size={16} /></span>}
      {children}
    </>
  );
}
